import React, { useState, useEffect } from "react";
import Iconify from "../../components/Iconify";
import { Button, Avatar, Tooltip } from "@mui/material";
import CustomTable from "../../components/customTable/CustomTable";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import StatusChip from "../../theme/chip";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Delete_Product, List_Products } from "../../DAL/Listing/ProductList";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import { formatNumber } from "../../utils/constant";
import { picBaseUrl } from "../../config/config";

const AdsList = () => {
  const { setnavbarTitle } = useAdminContext();
  const { enqueueSnackbar } = useSnackbar();
  const [rowDataID, setRowDataID] = useState("");
  const [rowData, setRowData] = useState({});
  const [openDelete, setOpenDelete] = useState(false);
  const [ProductsList, setProductsList] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Navigates to the "Add Product" page when triggered.
   *
   * This function is typically used to open the modal or redirect the user
   * to the page where they can add a new product to the product list.
   * It uses React Router's `navigate` function to redirect to the specific route.
   */
  const handleOpenModal = () => {
    navigate(`/product-list/add-product`);
  };

  /**
   * Handles the deletion of a product from the list.
   *
   * Initiates the deletion process by sending a request to remove the product
   * using its ID. If successful, it updates the product list by removing the deleted
   * product, adjusts the total count of products, and displays a success message.
   * If the deletion fails, it shows an error message.
   *
   * @returns {void}
   */
  const handleDelete = async () => {
    setIsLoading(true);
    const response = await Delete_Product(rowDataID);
    if (response) {
      const updatedUserList = ProductsList.filter(
        (user) => user._id !== rowDataID
      );
      setProductsList(updatedUserList);
      enqueueSnackbar(response.message, { variant: "success" });
      setTotalCount((prev) => prev - 1);
      setOpenDelete(false);
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setIsLoading(false);
  };

  /**
   * Handles the agreement to delete a product from the table.
   *
   * Sets the ID and data of the selected row to be deleted and opens the delete confirmation modal.
   * This allows the user to confirm or cancel the deletion action.
   *
   * @param {object} row - The data of the row (product) to be deleted.
   * @param {string} row._id - The unique ID of the product to be deleted.
   */
  const handleAgreeDelete = (row) => {
    setRowDataID(row._id);
    setRowData(row);
    setOpenDelete(true);
  };

  /**
   * Handles changes to the current page in the pagination controls.
   *
   * Updates the `page` state to the selected page number and adjusts the page count
   * based on the new page. The `newPage` parameter is a 0-based index, but for display purposes,
   * we increment it by 1 to show a 1-based page number.
   *
   * @param {object} event - The event object from the pagination component.
   * @param {number} newPage - The selected page number (0-based index).
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  /**
   * Handles changes to the number of rows displayed per page in the pagination controls.
   *
   * Updates the `rowsPerPage` state to reflect the new selection and resets the page to 0
   * to ensure that the user sees the first page of the new set of rows.
   *
   * @param {object} event - The event object from the rows per page selector.
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Handles page change for pagination controls.
   *
   * Converts the new page number from a 1-based index (UI) to a 0-based index (internal state),
   * which is commonly required for API requests or array operations.
   *
   * If the new page number is less than or equal to 0, it resets to the first page.
   *
   * @param {object} event - The event object from the pagination component.
   * @param {number} newPage - The selected page number from the UI (1-based index).
   */
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  /**
   * Column configuration for the Product List table.
   * Each object defines the properties and rendering logic for a specific column.
   *
   * Properties:
   * - id: Unique key for the column.
   * - label: Display name shown in the table header.
   * - type (optional): Used for formatting or identifying data types.
   * - renderData (optional): Custom render function for cell content.
   */
  const User_Table_Head = [
    {
      id: "action",
      label: "Action",
      renderData: (row) => (
        <>
          <Tooltip title="Edit" placement="top">
            <EditIcon
              onClick={() => {
                navigate(`/product-list/edit-product/${row._id}`, {
                  state: row,
                });
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Delete Product " placement="top">
            <DeleteOutlineIcon
              onClick={() => {
                handleAgreeDelete(row);
              }}
              style={{
                fill: "#ff0000",
                marginRight: "10px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    {
      id: "avatar",
      label: "Image",
      renderData: (row) => (
        <Avatar
          alt={row.name}
          src={picBaseUrl + row.image || "/default-avatar.png"}
          sx={{ width: 40, height: 40 }}
        />
      ),
    },
    {
      id: "title",
      label: "Title",
    },
    {
      id: "type",
      label: "Product Type",
      type: "type",
      renderData: (row) => (
        <span className=" text-capitalize">{row?.product_type || "_ _"}</span>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      renderData: (row) => <span>${formatNumber(row?.amount)}</span>,
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <StatusChip status={row.status} type="activeInactive" />
      ),
    },
  ];

  // This function fetches the product list based on search text and pagination parameters.
  // It sets loading state to true while fetching data.
  // On a successful API response (code === 200), it updates the product list, total pages, and total count.
  // On failure, it stops loading and shows an error message using enqueueSnackbar.
  const GetProductsList = async (page, rowsPerPage, searchText) => {
    setLoading(true);
    const FormData = {
      search: searchText,
    };
    const response = await List_Products(FormData, page, rowsPerPage);
    if (response.code === 200) {
      const { products_list, total_pages, total_count } = response;
      setProductsList(products_list);
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  // This useEffect sets the page title and fetches the product list.
  // It first checks localStorage for a saved search text ("searchText_Product_List").
  // If found, it uses that to fetch the products; otherwise, it uses the current searchText.
  // This runs whenever `page` or `rowsPerPage` changes.
  useEffect(() => {
    const searcthecxt = localStorage.getItem("searchText_Product_List");
    setnavbarTitle("Product List");
    if (searcthecxt) {
      GetProductsList(page, rowsPerPage, searcthecxt);
    } else {
      GetProductsList(page, rowsPerPage, searchText);
    }
  }, [page, rowsPerPage]);

  if (loading) {
    return <CustomCircularProgress />;
  }

  return (
    <div className="mt-4">
      <DeleteConfirmation
        open={openDelete}
        isLoading={isLoading}
        setRowData={setRowData}
        setRowDataID={setRowDataID}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this product?"}
        handleAgree={handleDelete}
        handleClose={() => setOpenDelete(false)}
      />

      {/* Add New User and Investigator Buttons */}
      <div className="d-flex justify-content-end mb-3 add-button">
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleOpenModal}
        >
          Create New Product
        </Button>
      </div>

      {/* User List Table */}
      <div className="mt-3">
        <CustomTable
          data={ProductsList}
          TABLE_HEAD={User_Table_Head}
          MENU_OPTIONS={[]}
          custom_pagination={{
            total_count: totalCount,
            rows_per_page: rowsPerPage,
            page: page,
            handleChangePage: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
          pageCount={pageCount}
          totalPages={totalPages}
          pagePagination={true}
          handleChangePages={handleChangePages}
          is_hide={true}
        />
      </div>
    </div>
  );
};

export default AdsList;
